import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { shopPublicService } from '../../../services/shop-public.service';
import { useShopStore } from '../../../redux/shop/facade';
import { StorefrontConfirmationComponent } from '../components/storefront-confirmation.component';
import type { ProformaStatus } from '../components/storefront-confirmation.component';

/**
 * StorefrontConfirmationContainer
 *
 * Handles screen 4 of 5 in the public storefront flow:
 *   /shop/confirmation?proforma=<uuid>
 *
 * Responsibilities:
 * - Parse the proforma UUID from the query string (multi-alias, matching payment-return-page pattern)
 * - Poll shopPublicService.proformaStatus(uuid) in local state (no Redux thunk needed)
 * - Render the appropriate confirmation state (paid / pending / expired / error)
 * - Provide the account-claim handlers to the presentational component
 *
 * NOTE (provider dependency): the payment provider must redirect the customer to
 *   /shop/confirmation?proforma=<uuid>
 * after a successful or cancelled payment. This must be configured on the admin side
 * per provider (Paystack return_url / callback_url). Flag for admin config follow-up.
 *
 * NOTE (deferred): the token returned by claimComplete is not wired into the app
 * auth session in V1. The account-claim prompt simply shows a success state.
 * Follow-up: wire token into Redux auth slice.
 */
export const StorefrontConfirmationContainer: React.FC = () => {
    const location = useLocation();

    // Multi-alias query param parsing — mirrors payment-return-page.container.tsx
    const searchParams = new URLSearchParams(location.search.replace(/&amp;/g, '&'));
    const hashParams = new URLSearchParams(location.hash.split('?')[1]?.replace(/&amp;/g, '&') || '');
    const getParam = (key: string) => searchParams.get(key) || hashParams.get(key);
    const proformaUuid =
        getParam('proforma') ||
        getParam('proformaId') ||
        getParam('proforma_id') ||
        getParam('proformaid') ||
        getParam('proformaUuid') ||
        getParam('proforma_uuid');

    const { paymentUrl, proforma, cart, clearCart, setPendingProforma } = useShopStore();

    const [status, setStatus] = useState<ProformaStatus>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [checkAgainCountdown, setCheckAgainCountdown] = useState(0);

    // The stored paymentUrl is a retry path for THIS order only: a guest who abandons
    // or cancels payment can resume the same proforma instead of rebuilding the cart
    // (which would create a duplicate order). Guard on the uuid so a stale paymentUrl
    // from a previous order is never resurfaced against this confirmation.
    const payAgainUrl =
        paymentUrl && proforma?.uuid && proforma.uuid === proformaUuid ? paymentUrl : undefined;

    const handlePayAgain = useCallback(() => {
        if (payAgainUrl) {
            window.location.href = payAgainUrl;
        }
    }, [payAgainUrl]);

    // Clear the cart + pending retry pointer once payment is CONFIRMED (not on redirect),
    // so a completed order doesn't leave stale cart items or a dangling pay-again path.
    useEffect(() => {
        if (status === 'paid' && (cart.length > 0 || paymentUrl || proforma)) {
            clearCart();
            setPendingProforma({});
        }
    }, [status, cart.length, paymentUrl, proforma, clearCart, setPendingProforma]);

    // Auto-poll on mount
    useEffect(() => {
        if (!proformaUuid) return;
        let cancelled = false;

        const fetchStatus = async () => {
            setIsFetching(true);
            setErrorMessage(undefined);
            try {
                const response = await shopPublicService.proformaStatus(proformaUuid);
                if (!cancelled) {
                    setStatus(response.results.status);
                }
            } catch (e: any) {
                if (!cancelled) {
                    setErrorMessage(e?.errorMessage ?? e?.message ?? 'Failed to check payment status.');
                }
            } finally {
                if (!cancelled) {
                    setIsFetching(false);
                }
            }
        };

        fetchStatus();
        return () => {
            cancelled = true;
        };
    }, [proformaUuid]);

    const handleCheckAgain = useCallback(async () => {
        if (!proformaUuid || isFetching) return;
        setIsFetching(true);
        setErrorMessage(undefined);
        try {
            const response = await shopPublicService.proformaStatus(proformaUuid);
            setStatus(response.results.status);
        } catch (e: any) {
            setErrorMessage(e?.errorMessage ?? e?.message ?? 'Failed to check payment status.');
        } finally {
            setIsFetching(false);
            setCheckAgainCountdown(0); // component manages its own display countdown
        }
    }, [proformaUuid, isFetching]);

    const handleClaimStart = useCallback(async (email: string) => {
        await shopPublicService.claimStart(email);
    }, []);

    const handleClaimComplete = useCallback(async (email: string, code: string, password: string) => {
        // V1: token returned but not wired into auth session.
        // Deferred follow-up: dispatch auth action with token from response.results.apiAuth.token
        await shopPublicService.claimComplete(email, code, password);
    }, []);

    return (
        <StorefrontConfirmationComponent
            proformaUuid={proformaUuid}
            status={status}
            isFetching={isFetching}
            errorMessage={errorMessage}
            onCheckAgain={handleCheckAgain}
            checkAgainCountdown={checkAgainCountdown}
            payAgainUrl={payAgainUrl}
            onPayAgain={handlePayAgain}
            onClaimStart={handleClaimStart}
            onClaimComplete={handleClaimComplete}
        />
    );
};

export default StorefrontConfirmationContainer;
